import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '../config/api';
import { trackEvent } from '../utils/analytics';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { formatISODate } from '../components/badges';
import { csvCodec, useUrlState } from '../hooks/useUrlState';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  GroupLimit,
  JurisdictionInfo,
  JurisdictionMeta,
  Limit,
  MARKER_TITLES,
  MappingInfo,
  ParsedMrl,
  VerdictResult,
  judge,
  limitToParsed,
  mrlText,
  normName,
  parseLabReport,
  parseMrl,
  parseUserValue,
  shortReg,
  shortTox,
  splitResidueName,
  tidyName,
  verdictLabel,
} from '../utils/pesticide';

/* ── API shapes ─────────────────────────────────────────────────────── */

interface PathNode {
  product_id: string;
  product_name: string | null;
  product_code: string | null;
}

interface SubstanceMrl {
  pesticide_residue_id: string;
  name: string;
  substance_status: string | null;
  adi: string | null;
  arfd: string | null;
  limits: Record<string, Limit | null>;
  unmapped: Record<string, boolean>;
}

interface ProductData {
  product: { product_id: string; product_name: string | null; product_code: string | null };
  path: PathNode[];
  jurisdictions: JurisdictionMeta[];
  ignored_jurisdictions: string[];
  substances: SubstanceMrl[];
}

interface ProductHit {
  product_id: string;
  product_name: string;
  product_code: string | null;
}

/* ── View model ─────────────────────────────────────────────────────── */

interface Cell {
  limit: Limit | null;
  mrl: ParsedMrl;
  upMrl: ParsedMrl | null;
  unmapped: boolean;
}

interface Row {
  pesticide_residue_id: string;
  name: string;
  substance_status: string | null;
  adi: string | null;
  arfd: string | null;
  display: string;
  markers: string[];
  banned: boolean;
  baseKey: string;
  fullKey: string;
  cells: Record<string, Cell>;
}

interface Check {
  result: VerdictResult | null;
  invalid: boolean;
  upcomingFail: boolean;
  unmapped: boolean;
  /** A residue was found (value above zero, not a "<LOQ" bound). */
  detected: boolean;
}

interface Summary {
  pass: number;
  limit: number;
  fail: number;
  upcomingFail: number;
  invalid: number;
  inconclusive: number;
  unassessed: number;
  /** Non-EU only: residue detected, no tolerance on record. Not a pass. */
  detectedNoLimit: number;
  /** Non-EU only: no item tolerance, but a crop-group tolerance exists. Not judged. */
  groupOnly: number;
}

type View = 'set' | 'all' | 'entered';

const KIND_RANK: Record<ParsedMrl['kind'], number> = { set: 0, lod: 1, exempt: 2, none: 3 };
const NO_CHECK: Check = { result: null, invalid: false, upcomingFail: false, unmapped: false, detected: false };
const NO_CELL: Cell = { limit: null, mrl: { raw: null, kind: 'none', value: null }, upMrl: null, unmapped: false };

// The selected legislation lives in the URL (?j=EU,US) so a shipment check is
// shareable, and is remembered per browser for the next product.
const URL_CONFIG = { j: csvCodec(['EU']) };
const JURISDICTION_STORAGE_KEY = 'fsnu_jurisdictions';

const CATEGORY_LABELS: Record<string, string> = {
  regional: 'Regional registration only',
  emergency: 'Section 18 emergency exemption, time-limited',
  indirect: 'Indirect or inadvertent residues',
  exemption: 'Exemption from the requirement of a tolerance',
};

function readStoredJurisdictions(): string[] | null {
  try {
    const raw = window.localStorage.getItem(JURISDICTION_STORAGE_KEY);
    const list = (raw ?? '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    return list.length > 0 ? list : null;
  } catch {
    return null;
  }
}

function storeJurisdictions(codes: string[]) {
  try {
    window.localStorage.setItem(JURISDICTION_STORAGE_KEY, codes.join(','));
  } catch {
    /* private mode or storage disabled: the URL still carries the choice */
  }
}

async function fetchJurisdictions(): Promise<JurisdictionInfo[]> {
  const res = await fetch(`${API_BASE_URL}/api/jurisdictions`);
  if (!res.ok) throw new Error(`Could not load jurisdictions (HTTP ${res.status}).`);
  const json = await res.json();
  return json.jurisdictions ?? [];
}

async function fetchProduct(productId: string, codes: string[]): Promise<ProductData> {
  const qs = `?jurisdictions=${encodeURIComponent(codes.join(','))}`;
  const res = await fetch(`${API_BASE_URL}/api/product/${encodeURIComponent(productId)}${qs}`);
  if (res.status === 404) throw new Error('This product is not in the MRL database.');
  if (res.status === 400) {
    const body = await res.json().catch(() => null);
    throw new Error(body?.error ?? 'That legislation selection is not available.');
  }
  if (!res.ok) throw new Error(`Could not load this product (HTTP ${res.status}).`);
  const json = await res.json();
  if (!json.ok) throw new Error('The server could not load this product.');
  return {
    product: json.product,
    path: json.path ?? [],
    jurisdictions: json.jurisdictions ?? [],
    ignored_jurisdictions: json.ignored_jurisdictions ?? [],
    substances: json.substances ?? [],
  };
}

function cellRank(r: Row, codes: string[]): number {
  return Math.min(...codes.map(c => {
    const cell = r.cells[c];
    return !cell || cell.unmapped ? KIND_RANK.none : KIND_RANK[cell.mrl.kind];
  }));
}

/** The "Set MRLs" rule, shared by the chip count and the row filter. */
function hasSetLimit(r: Row, codes: string[]): boolean {
  return codes.some(c => !r.cells[c]?.unmapped && r.cells[c]?.mrl.kind === 'set');
}

function toRows(list: SubstanceMrl[], codes: string[]): Row[] {
  return list
    .map(s => {
      const { name, markers } = splitResidueName(s.name);
      const cells: Record<string, Cell> = {};
      for (const code of codes) {
        const limit = s.limits?.[code] ?? null;
        cells[code] = {
          limit,
          mrl: limitToParsed(limit),
          upMrl: limit?.upcoming ? parseMrl(limit.upcoming.value_text) : null,
          unmapped: !!s.unmapped?.[code],
        };
      }
      return {
        pesticide_residue_id: s.pesticide_residue_id,
        name: s.name,
        substance_status: s.substance_status,
        adi: s.adi,
        arfd: s.arfd,
        display: name,
        markers,
        banned: s.substance_status === 'Not approved',
        baseKey: normName(name.split('(')[0]),
        fullKey: normName(name),
        cells,
      };
    })
    .sort((a, b) =>
      cellRank(a, codes) - cellRank(b, codes)
      || Number(a.banned) - Number(b.banned)
      || a.display.localeCompare(b.display));
}

/** Judge one value against one jurisdiction's limit. An unmapped cell never yields a verdict. */
function checkCell(cell: Cell | undefined, raw: string | undefined): Check {
  if (!cell) return NO_CHECK;
  if (raw == null || raw.trim() === '') return { ...NO_CHECK, unmapped: cell.unmapped };
  const value = parseUserValue(raw);
  if (value == null) return { ...NO_CHECK, invalid: true, unmapped: cell.unmapped };
  const detected = value.value > 0 && !value.lessThan;
  if (cell.unmapped) return { ...NO_CHECK, unmapped: true, detected };
  const result = judge(value, cell.mrl);
  const upcomingFail = !!cell.upMrl && result.verdict !== 'fail' && judge(value, cell.upMrl).verdict === 'fail';
  return { result, invalid: false, upcomingFail, unmapped: false, detected };
}

/**
 * Match a pasted substance name to a row. Exact base name first, then a prefix
 * that ends on a word boundary: a loose prefix matches "2,4-DB" for "2,4-D" and
 * would score the value against the wrong MRL. Ambiguous names match nothing.
 */
function matchRow(rows: Row[], name: string): Row | undefined {
  const key = normName(name);
  if (!key) return undefined;
  const exact = rows.filter(r => r.baseKey === key);
  if (exact.length > 0) return exact.length === 1 ? exact[0] : undefined;
  const prefixed = rows.filter(r => r.baseKey.startsWith(key + ' '));
  return prefixed.length === 1 ? prefixed[0] : undefined;
}

function unit(m: ParsedMrl | null): string {
  return m && (m.kind === 'set' || m.kind === 'lod') ? ' mg/kg' : '';
}

/** Cell text: the EU says "No MRL required", other registers "Exempt". */
function limitText(m: ParsedMrl, code: string): string {
  if (m.kind === 'exempt') return code === 'EU' ? 'No MRL required' : 'Exempt';
  return mrlText(m);
}

function verdictText(r: VerdictResult, code: string): string {
  if (code === 'EU') return verdictLabel(r);
  if (r.verdict === 'exempt') return 'Exempt';
  if (r.verdict === 'nolimit') return `No ${code} tolerance`;
  return verdictLabel(r);
}

function refLabel(code: string, ref: string): string {
  return code === 'EU' ? shortReg(ref) : ref.replace(/^40 CFR\s+/, '§ ');
}

function unmappedTitle(meta: JurisdictionMeta | undefined, code: string): string {
  const name = meta?.name ?? code;
  const pending = meta?.pending_review ? ' A proposed mapping is waiting for editor review.' : '';
  return `No ${name} commodity is mapped to this product yet, so no ${code} conclusion is possible. `
    + `This does not mean the substance is unregulated there.${pending}`;
}

function mappingText(m: MappingInfo | null): string {
  if (!m) return 'by an editor';
  switch (m.method) {
    case 'cas': return 'by CAS number';
    case 'name': return 'by name';
    case 'llm': return `by assisted match (${Math.round((m.confidence ?? 0) * 100)}% confidence)`;
    default: return 'by an editor';
  }
}

function commodityMappingText(meta: JurisdictionMeta | undefined): string {
  const c = meta?.commodity;
  if (!c) return '';
  return mappingText({ method: c.match_method, confidence: c.match_confidence, reviewed: true });
}

/* ── Product switcher (search by name or code) ──────────────────────── */

function ProductSwitcher({ currentId }: { currentId: string }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<ProductHit[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      setOpen(false);
      setLoading(false);
      return;
    }
    const ctl = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/pesticides/search-by-product?q=${encodeURIComponent(q)}&status=all`,
          { signal: ctl.signal },
        );
        if (res.ok) {
          const json = await res.json();
          setHits(((json.results ?? []) as ProductHit[]).filter(h => h.product_id !== currentId));
          setActive(0);
          setOpen(true);
        }
      } catch {
        /* aborted or offline: keep the previous list */
      } finally {
        if (!ctl.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      ctl.abort();
    };
  }, [query, currentId]);

  function go(hit: ProductHit) {
    setOpen(false);
    setQuery('');
    // Keep the legislation selection when switching product.
    navigate(`/product/${encodeURIComponent(hit.product_id)}${window.location.search}`);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open || hits.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(i => Math.min(i + 1, hits.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(hits[active]);
    }
  }

  return (
    <div className="pest-switcher" ref={wrapRef}>
      <svg className="pest-switcher__icon" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M20 20l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <input
        type="search"
        className="pest-switcher__input"
        placeholder="Change product — type a name or code, e.g. pears, 0130020"
        aria-label="Change product"
        role="combobox"
        aria-expanded={open}
        aria-controls="pest-switcher-list"
        aria-autocomplete="list"
        aria-activedescendant={open && hits.length > 0 ? `pest-hit-${active}` : undefined}
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => hits.length > 0 && setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {loading && <span className="pest-search__spinner" />}
      {open && (hits.length > 0 ? (
        <ul className="pest-search__dropdown" id="pest-switcher-list" role="listbox">
          {hits.map((h, i) => (
            <li
              key={h.product_id}
              id={`pest-hit-${i}`}
              role="option"
              aria-selected={i === active}
              className={`pest-search__option${i === active ? ' pest-search__option--active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={e => {
                e.preventDefault();
                go(h);
              }}
            >
              <span className="pest-search__name">{h.product_name}</span>
              {h.product_code && <span className="pest-search__reg">{h.product_code}</span>}
            </li>
          ))}
        </ul>
      ) : (
        <div className="pest-search__dropdown pest-search__empty">No product matches.</div>
      ))}
    </div>
  );
}

/* ── Expanded row: provenance per jurisdiction ──────────────────────── */

function ForeignDetail({ code, row, meta, groups }: {
  code: string;
  row: Row;
  meta: JurisdictionMeta | undefined;
  groups: GroupLimit[];
}) {
  const cell = row.cells[code] ?? NO_CELL;
  const limit = cell.limit;
  const name = meta?.name ?? code;
  return (
    <div className="pest-detail__wide">
      <dt>{name}</dt>
      <dd>
        {cell.unmapped ? (
          <div className="pest-detail__note">{unmappedTitle(meta, code)}</div>
        ) : limit ? (
          <>
            <div>
              <strong className="pest-mono">{limitText(cell.mrl, code)}{unit(cell.mrl)}</strong>
              {limit.legal_ref && (
                <>
                  {' · '}
                  {limit.reference_url ? (
                    <a href={limit.reference_url} target="_blank" rel="noopener noreferrer">{limit.legal_ref} ↗</a>
                  ) : (
                    limit.legal_ref
                  )}
                </>
              )}
            </div>
            <div className="pest-detail__sub">
              Commodity: {limit.commodity_name}
              {meta?.inherited_from && ` (via ${tidyName(meta.inherited_from.product_name) || 'a parent group'})`}
              {limit.category && CATEGORY_LABELS[limit.category] && ` · ${CATEGORY_LABELS[limit.category]}`}
            </div>
            {limit.applicability && (
              <div className="pest-detail__warn">Applies only: {limit.applicability}. The verdict does not check this condition.</div>
            )}
            {limit.expiry_date && <div className="pest-detail__sub">Expires {formatISODate(limit.expiry_date)}</div>}
            {limit.footnote && <div className="pest-detail__sub">Note: {limit.footnote}</div>}
            {limit.effective_date && (
              <div className="pest-detail__sub">Section last amended {formatISODate(limit.effective_date)}</div>
            )}
            <div className="pest-detail__sub">
              Substance matched {mappingText(limit.mapping)}; commodity matched {commodityMappingText(meta)}.
            </div>
            {limit.alternates.length > 0 && (
              <div className="pest-detail__sub">
                Also listed: {limit.alternates
                  .map(a => `${a.commodity_name} ${a.value_text ?? ''}${a.legal_ref ? ` (${refLabel(code, a.legal_ref)})` : ''}`)
                  .join('; ')}
              </div>
            )}
          </>
        ) : (
          <div>No {name} tolerance on record for this substance on {meta?.commodity?.name ?? 'this commodity'}.</div>
        )}
        {groups.length > 0 && (
          <div className="pest-detail__sub">
            Applies via commodity group, not used for the verdict: {groups
              .map(g => `${g.group_commodity}: ${g.is_exempt ? 'exempt' : g.value_text ?? '—'} (${refLabel(code, g.legal_ref)})`)
              .join('; ')}
          </div>
        )}
        {meta?.attribution && <div className="pest-attribution">{meta.attribution}</div>}
      </dd>
    </div>
  );
}

function RowDetail({ row, codes, checks, metaByCode, groupsByResidue }: {
  row: Row;
  codes: string[];
  checks: Record<string, Check>;
  metaByCode: Map<string, JurisdictionMeta>;
  groupsByResidue: Map<string, GroupLimit[]>;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const eu = codes.includes('EU') ? row.cells.EU ?? NO_CELL : null;
  const euLimit = eu?.limit ?? null;
  const multi = codes.length > 1;
  const applicabilityText = euLimit?.applicability?.trim() ?? '';
  const staleNotYet = /not yet applicable/i.test(applicabilityText)
    && !!euLimit?.effective_date && euLimit.effective_date <= today;
  const applicability = applicabilityText && !/^applicable$/i.test(applicabilityText) && !staleNotYet
    ? applicabilityText
    : null;
  return (
    <dl className="pest-detail__grid">
      {eu && (
        <>
          <div>
            <dt>{multi ? 'EU regulation' : 'Regulation'}</dt>
            <dd>
              {euLimit?.legal_ref ? (
                <>
                  <Link to={`/regulation/${encodeURIComponent(euLimit.legal_ref)}`}>{euLimit.legal_ref}</Link>
                  {euLimit.reference_url && (
                    <>
                      {' · '}
                      <a href={euLimit.reference_url} target="_blank" rel="noopener noreferrer">EUR-Lex ↗</a>
                    </>
                  )}
                </>
              ) : (
                'No MRL in force today'
              )}
              {euLimit?.effective_date && (
                <div className="pest-detail__sub">Applies from {formatISODate(euLimit.effective_date)}</div>
              )}
            </dd>
          </div>
          {euLimit?.upcoming && eu.upMrl && (
            <div>
              <dt>Upcoming change</dt>
              <dd>
                {mrlText(eu.upMrl)}{unit(eu.upMrl)} from {formatISODate(euLimit.upcoming.effective_date)}
                {euLimit.upcoming.legal_ref && (
                  <div className="pest-detail__sub">
                    <Link to={`/regulation/${encodeURIComponent(euLimit.upcoming.legal_ref)}`}>
                      {shortReg(euLimit.upcoming.legal_ref)}
                    </Link>
                  </div>
                )}
                {checks.EU?.upcomingFail && <div className="pest-detail__warn">Your value fails this upcoming MRL.</div>}
              </dd>
            </div>
          )}
          <div>
            <dt>Residue definition</dt>
            <dd>{euLimit?.residue_definition || row.name}</dd>
          </div>
          {euLimit?.footnote && (
            <div className="pest-detail__wide">
              <dt>Footnote</dt>
              <dd>{euLimit.footnote}</dd>
            </div>
          )}
          {applicability && (
            <div>
              <dt>Applicability</dt>
              <dd>{applicability}</dd>
            </div>
          )}
        </>
      )}
      {codes.filter(c => c !== 'EU').map(code => (
        <ForeignDetail
          key={code}
          code={code}
          row={row}
          meta={metaByCode.get(code)}
          groups={groupsByResidue.get(`${code}|${row.pesticide_residue_id}`) ?? []}
        />
      ))}
      <div>
        <dt>Substance</dt>
        <dd>
          <Link to={`/substance/${encodeURIComponent(row.pesticide_residue_id)}`}>Full substance profile</Link>
          {row.substance_status && <div className="pest-detail__sub">EU approval: {row.substance_status}</div>}
        </dd>
      </div>
    </dl>
  );
}

/* ── Shipment summary, one line per jurisdiction ────────────────────── */

function SummaryPills({ code, s, meta }: { code: string; s: Summary; meta: JurisdictionMeta | undefined }) {
  if (meta?.unmapped) {
    return (
      <span className="pest-pill pest-pill--unmapped" title={unmappedTitle(meta, code)}>
        Commodity not mapped · no {code} conclusion
      </span>
    );
  }
  return (
    <>
      <span className="pest-pill pest-pill--pass">{s.pass} pass</span>
      <span className="pest-pill pest-pill--limit">{s.limit} at limit</span>
      <span className="pest-pill pest-pill--fail">{s.fail} fail</span>
      {s.inconclusive > 0 && (
        <span className="pest-pill pest-pill--inconclusive">{s.inconclusive} inconclusive</span>
      )}
      {s.detectedNoLimit > 0 && (
        <span className="pest-pill pest-pill--notol">
          {s.detectedNoLimit} detected without {code} tolerance
        </span>
      )}
      {s.groupOnly > 0 && (
        <span className="pest-pill pest-pill--group">
          {s.groupOnly} covered only by a crop-group tolerance · not judged
        </span>
      )}
      {s.unassessed > 0 && (
        <span className="pest-pill pest-pill--muted">
          {s.unassessed} {code === 'EU' ? 'no MRL on record' : `no ${code} tolerance on record`}
        </span>
      )}
      {code === 'EU' && (
        <span className="pest-pill pest-pill--muted">{s.upcomingFail} fail after upcoming change</span>
      )}
      {s.invalid > 0 && <span className="pest-pill pest-pill--muted">{s.invalid} unreadable</span>}
      {meta && meta.unmatched_total > 0 && (
        <span className="pest-pill pest-pill--muted">
          {meta.unmatched_total} {code} tolerance{meta.unmatched_total === 1 ? '' : 's'} not matched to EU substances (listed above the table)
        </span>
      )}
    </>
  );
}

/* ── Page ───────────────────────────────────────────────────────────── */

export default function ProductPage() {
  const { productId = '' } = useParams<{ productId: string }>();
  const [searchParams] = useSearchParams();
  const { state: urlState, set: setUrl } = useUrlState(URL_CONFIG);

  const jurisdictionQuery = useQuery<JurisdictionInfo[], Error>(['jurisdictions'], fetchJurisdictions, {
    staleTime: 300_000,
  });
  const jurisdictionList = jurisdictionQuery.data;
  const available = useMemo(() => jurisdictionList?.map(j => j.code) ?? [], [jurisdictionList]);

  // A bare /product/:id restores the last selection made in this browser. It is
  // read on the first render so the product request starts with it at once,
  // in parallel with the jurisdiction list; the server drops a register that
  // was switched off in the meantime and reports it as ignored.
  const stored = useMemo(() => (searchParams.has('j') ? null : readStoredJurisdictions()), [searchParams]);
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (stored && !(stored.length === 1 && stored[0] === 'EU')) setUrl('j', stored);
  }, [stored, setUrl]);

  const selected = useMemo(() => {
    const wanted = stored && !restoredRef.current ? stored : urlState.j;
    return wanted.length > 0 ? wanted : ['EU'];
  }, [stored, urlState.j]);
  const selectedKey = selected.join(',');

  const { data: fetched, isError, error, isFetching, refetch } = useQuery<ProductData, Error>(
    ['product', productId, selectedKey],
    () => fetchProduct(productId, selected),
    { enabled: !!productId, keepPreviousData: true },
  );
  // keepPreviousData smooths a jurisdiction toggle but must never show another
  // product; and a failed toggle keeps this product's last good data on screen,
  // so the lab values the user typed survive.
  const lastGoodRef = useRef<{ productId: string; data: ProductData } | null>(null);
  if (fetched && fetched.product.product_id === productId) lastGoodRef.current = { productId, data: fetched };
  const data = fetched && fetched.product.product_id === productId
    ? fetched
    : lastGoodRef.current?.productId === productId
    ? lastGoodRef.current.data
    : undefined;
  const fetchFailed = isError && !!data;

  const [view, setView] = useState<View>('set');
  const [filter, setFilter] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteNote, setPasteNote] = useState<string | null>(null);

  // A different product starts a fresh check: entered values belong to one shipment.
  useEffect(() => {
    setView('set');
    setFilter('');
    setValues({});
    setOpenId(null);
    setPasteOpen(false);
    setPasteText('');
    setPasteNote(null);
  }, [productId]);

  useEffect(() => {
    if (data) trackEvent('Product View', { product: productId });
  }, [data, productId]);

  const metaName = data ? tidyName(data.product.product_name) : null;
  usePageMeta({
    title: metaName ? `${metaName}: pesticide MRLs` : 'Product',
    description: metaName
      ? `Pesticide maximum residue levels (MRLs) on ${metaName}: current EU limits, other jurisdictions alongside, and a check for lab results.`
      : null,
    noindex: !data && isError,
  });

  // Remember a selection only once it has loaded, so a broken one is never restored.
  useEffect(() => {
    if (fetched && fetched.product.product_id === productId) {
      storeJurisdictions(fetched.jurisdictions.map(j => j.code));
    }
  }, [fetched, productId]);

  const codes = useMemo(() => data?.jurisdictions.map(j => j.code) ?? [], [data]);
  const metaByCode = useMemo(() => new Map((data?.jurisdictions ?? []).map(j => [j.code, j])), [data]);
  const groupsByResidue = useMemo(() => {
    const m = new Map<string, GroupLimit[]>();
    for (const j of data?.jurisdictions ?? []) {
      for (const g of j.group_limits) {
        const key = `${j.code}|${g.pesticide_residue_id}`;
        const list = m.get(key) ?? [];
        list.push(g);
        m.set(key, list);
      }
    }
    return m;
  }, [data]);

  const rows = useMemo(() => (data ? toRows(data.substances, codes) : []), [data, codes]);

  const checks = useMemo(() => {
    const m = new Map<string, Record<string, Check>>();
    for (const r of rows) {
      const byCode: Record<string, Check> = {};
      for (const code of codes) byCode[code] = checkCell(r.cells[code], values[r.pesticide_residue_id]);
      m.set(r.pesticide_residue_id, byCode);
    }
    return m;
  }, [rows, codes, values]);

  const counts = useMemo(() => {
    let set = 0;
    let entered = 0;
    for (const r of rows) {
      if (hasSetLimit(r, codes)) set++;
      if ((values[r.pesticide_residue_id] ?? '').trim() !== '') entered++;
    }
    return { set, all: rows.length, entered };
  }, [rows, codes, values]);

  const hasGroupLimit = (code: string, rid: string) => (groupsByResidue.get(`${code}|${rid}`)?.length ?? 0) > 0;

  const summary = useMemo(() => {
    const out: Record<string, Summary> = {};
    for (const code of codes) {
      out[code] = {
        pass: 0, limit: 0, fail: 0, upcomingFail: 0, invalid: 0, inconclusive: 0, unassessed: 0,
        detectedNoLimit: 0, groupOnly: 0,
      };
    }
    checks.forEach((byCode, rid) => {
      for (const code of codes) {
        const c = byCode[code];
        const s = out[code];
        if (!c) continue;
        if (c.invalid) s.invalid++;
        if (!c.result) continue;
        if (c.result.verdict === 'pass' || c.result.verdict === 'exempt') s.pass++;
        else if (c.result.verdict === 'limit') s.limit++;
        else if (c.result.verdict === 'fail') s.fail++;
        else if (c.result.verdict === 'inconclusive') s.inconclusive++;
        else if (c.result.verdict === 'nolimit') {
          // A crop-group tolerance is never judged: the row says so instead of "no tolerance".
          if (code !== 'EU' && (groupsByResidue.get(`${code}|${rid}`)?.length ?? 0) > 0) s.groupOnly++;
          else if (code !== 'EU' && c.detected) s.detectedNoLimit++;
          else s.unassessed++;
        }
        if (c.upcomingFail) s.upcomingFail++;
      }
    });
    return out;
  }, [checks, codes, groupsByResidue]);

  const shown = useMemo(() => {
    const term = normName(filter);
    return rows.filter(r => {
      if (view === 'set' && !hasSetLimit(r, codes)) return false;
      if (view === 'entered' && values[r.pesticide_residue_id] === undefined) return false;
      if (term && !r.fullKey.includes(term)) return false;
      return true;
    });
  }, [rows, codes, view, filter, values]);

  function toggleJurisdiction(code: string) {
    const next = selected.includes(code)
      ? selected.filter(c => c !== code)
      : available.filter(c => c === code || selected.includes(c));
    if (next.length === 0) return;
    setUrl('j', next);
    trackEvent('Jurisdiction Select', { jurisdictions: next.join(',') });
  }

  function setValue(id: string, v: string) {
    // Keep the key when the box is emptied. The Entered view filters on the key,
    // so dropping it would unmount the input the user is still typing in.
    setValues(prev => ({ ...prev, [id]: v }));
  }

  function applyPaste() {
    const { parsed, skipped } = parseLabReport(pasteText);
    const next = { ...values };
    const missing: string[] = [];
    const collided: string[] = [];
    const usedBy = new Map<string, string>();
    let matched = 0;
    for (const line of parsed) {
      const row = matchRow(rows, line.name);
      if (!row) {
        missing.push(line.name);
        continue;
      }
      const already = usedBy.get(row.pesticide_residue_id);
      if (already) {
        collided.push(`${line.name} and ${already}`);
        continue;
      }
      usedBy.set(row.pesticide_residue_id, line.name);
      next[row.pesticide_residue_id] = line.token;
      matched++;
    }
    setValues(next);
    const total = parsed.length + skipped.length;
    const parts = [`Applied ${matched} of ${total} line${total === 1 ? '' : 's'}.`];
    if (missing.length) parts.push(`Not found for this product: ${missing.join(', ')}.`);
    if (collided.length) {
      parts.push(`Two lines matched the same substance, the later one ignored: ${collided.join('; ')}.`);
    }
    if (skipped.length) parts.push(`Could not read ${skipped.length} line${skipped.length === 1 ? '' : 's'}.`);
    setPasteNote(parts.join(' '));
    if (matched > 0) {
      setView('entered');
      setPasteOpen(false);
      setPasteText('');
    }
    trackEvent('Lab Report Paste', { product: productId, lines: String(total), matched: String(matched) });
  }

  function clearValues() {
    setValues({});
    setPasteNote(null);
    if (view === 'entered') setView('set');
  }

  if (!productId) return <p className="pest-page">No product specified.</p>;
  if (!data && !isError) return <LoadingSkeleton rows={8} />;
  if (!data) {
    const foreignSelected = selected.filter(c => c !== 'EU');
    return (
      <div className="error-state">
        <div>{error?.message ?? 'Could not load this product.'}</div>
        {foreignSelected.length > 0 && (
          <div className="pest-detail__sub">
            <button
              type="button"
              className="pest-btn"
              onClick={() => {
                storeJurisdictions(['EU']);
                setUrl('j', ['EU']);
              }}
            >
              Check against EU only
            </button>
          </div>
        )}
      </div>
    );
  }

  const { product, path } = data;
  const title = tidyName(product.product_name) || 'Product';
  const crumbs = path.length > 1 ? path : [];
  const hasEu = codes.includes('EU');
  const onlyEu = codes.length === 1 && hasEu;
  const foreignCodes = codes.filter(c => c !== 'EU');
  const colCount = 1 + codes.length + 1 + codes.length + (hasEu ? 1 : 0) + 2;
  const unmappedCodes = foreignCodes.filter(c => metaByCode.get(c)?.unmapped);
  const checkable = codes.filter(c => !metaByCode.get(c)?.unmapped);
  const allUnmapped = codes.length > 0 && checkable.length === 0;

  const emptyText = allUnmapped
    ? `No ${unmappedCodes.join(' or ')} commodity is mapped to this product yet, so no ${unmappedCodes.join(' or ')} limits can be shown. This does not mean none apply. Add EU to see the EU limits.`
    : rows.length === 0
    ? 'No MRLs are published for this product.'
    : filter.trim()
    ? 'No substance matches the filter.'
    : view === 'set'
    ? hasEu
      ? 'No specific MRLs for this product. Choose All to see LOD defaults.'
      : `No specific ${checkable.join(', ')} tolerances for this product. Choose All to see every substance.`
    : view === 'entered'
    ? 'No values entered yet. Type a lab result under Your value, or paste a lab report.'
    : 'No substance matches.';

  const views: Array<[View, string]> = [
    ['set', `Set MRLs (${counts.set})`],
    ['all', `All (${counts.all})`],
    ['entered', `Entered (${counts.entered})`],
  ];

  return (
    <div className="pest-page">
      <div className="pest-page__head">
        <div>
          <div className="pest-eyebrow">
            Product{product.product_code ? ` · Code ${product.product_code}` : ''}
          </div>
          <h1 className="pest-page__title">{title}</h1>
        </div>
        {crumbs.length > 0 && (
          <nav className="pest-crumbs" aria-label="Product hierarchy">
            {crumbs.map((c, i) => (
              <React.Fragment key={c.product_id}>
                {i > 0 && <span className="pest-crumbs__sep" aria-hidden="true">›</span>}
                <span className={i === crumbs.length - 1 ? 'pest-crumbs__current' : undefined}>
                  {tidyName(c.product_name)}
                </span>
              </React.Fragment>
            ))}
          </nav>
        )}
      </div>

      <ProductSwitcher currentId={product.product_id} />

      {fetchFailed && (
        <p className="pest-jur-note pest-jur-note--error" role="alert">
          The {selectedKey.replace(/,/g, ' + ')} limits could not be loaded ({error?.message ?? 'request failed'}).
          Showing the last loaded selection ({codes.join(' + ')}); your values are kept.{' '}
          <button type="button" className="pest-btn" onClick={() => refetch()}>Retry</button>{' '}
          {selected.some(c => c !== 'EU') && (
            <button type="button" className="pest-btn" onClick={() => setUrl('j', ['EU'])}>Check against EU only</button>
          )}
        </p>
      )}
      {data.ignored_jurisdictions.length > 0 && (
        <p className="pest-jur-note" role="note">
          {data.ignored_jurisdictions.join(', ')} is switched off at the moment and was left out of this check.
        </p>
      )}

      <section className="pest-card" aria-label={`MRLs applying to ${title}`}>
        <div className="pest-card__head">
          <h2 className="pest-card__title">MRLs applying to {title} · {shown.length}</h2>
          <div className="pest-card__controls">
            {(jurisdictionList?.length ?? 0) > 1 && (
              <div className="pest-chips" role="group" aria-label="Legislation to check against">
                <span className="pest-chips__label">Legislation</span>
                {jurisdictionList!.map(j => (
                  <button
                    key={j.code}
                    type="button"
                    className={`pest-chip${selected.includes(j.code) ? ' pest-chip--active' : ''}`}
                    aria-pressed={selected.includes(j.code)}
                    title={j.name}
                    onClick={() => toggleJurisdiction(j.code)}
                  >
                    {j.code}
                  </button>
                ))}
                {isFetching && <span className="pest-updating" role="status">Updating…</span>}
              </div>
            )}
            <div className="pest-chips" role="group" aria-label="Rows to show">
              {views.map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  className={`pest-chip${view === v ? ' pest-chip--active' : ''}`}
                  aria-pressed={view === v}
                  onClick={() => setView(v)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="pest-toolbar">
          <input
            type="search"
            className="pest-toolbar__filter"
            placeholder="Filter substance..."
            aria-label="Filter substances"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          <button
            type="button"
            className="pest-btn pest-btn--primary"
            aria-expanded={pasteOpen}
            onClick={() => setPasteOpen(o => !o)}
          >
            Paste lab report
          </button>
          <button type="button" className="pest-btn" disabled={counts.entered === 0} onClick={clearValues}>
            Clear values
          </button>
        </div>

        {foreignCodes.map(code => {
          const meta = metaByCode.get(code);
          if (!meta?.unmapped) return null;
          return (
            <p key={code} className="pest-jur-note" role="note">
              <span className="pest-pill pest-pill--unmapped">{code} not mapped</span> {unmappedTitle(meta, code)}
            </p>
          );
        })}
        {foreignCodes.map(code => {
          const meta = metaByCode.get(code);
          if (!meta || meta.unmatched_total === 0) return null;
          const more = meta.unmatched_total - meta.unmatched_substances.length;
          return (
            <details key={`unmatched-${code}`} className="pest-groups">
              <summary>
                {meta.unmatched_total} {code} tolerance{meta.unmatched_total === 1 ? '' : 's'} on{' '}
                {meta.commodity?.name ?? 'this commodity'} belong to substances not yet matched to an EU substance,
                so they have no row below
              </summary>
              <ul className="pest-groups__list">
                {meta.unmatched_substances.map(u => (
                  <li key={u.substance_key}>
                    <strong>{u.name}</strong>
                    {' · '}<span className="pest-mono">{u.value_text ?? '—'} mg/kg</span>
                    {' · '}{refLabel(code, u.legal_ref)}
                  </li>
                ))}
                {more > 0 && <li>and {more} more</li>}
              </ul>
            </details>
          );
        })}

        {pasteOpen && (
          <div className="pest-paste">
            <label className="pest-paste__label" htmlFor="pest-paste-text">
              Paste one result per line: the substance name, then the value in mg/kg.
              {' "nd" and "<0.01" count as not detected.'}
            </label>
            <textarea
              id="pest-paste-text"
              className="pest-paste__text"
              rows={5}
              value={pasteText}
              onChange={e => setPasteText(e.target.value)}
              placeholder={'Captan\t2.1\nAcetamiprid; 0.4\nGlyphosate <0.05'}
            />
            <div className="pest-paste__actions">
              <button type="button" className="pest-btn pest-btn--solid" disabled={!pasteText.trim()} onClick={applyPaste}>
                Apply values
              </button>
              <button type="button" className="pest-btn" onClick={() => setPasteOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
        {pasteNote && <p className="pest-paste__note" role="status">{pasteNote}</p>}

        <div className="pest-table-wrap">
          <table className="pest-table">
            <thead>
              <tr>
                <th scope="col">Substance</th>
                {codes.map(code => (
                  <th key={`mrl-${code}`} scope="col">{onlyEu ? 'MRL mg/kg' : `MRL ${code} mg/kg`}</th>
                ))}
                <th scope="col">Your value</th>
                {codes.map(code => (
                  <th key={`verdict-${code}`} scope="col">{onlyEu ? 'Verdict' : `Verdict ${code}`}</th>
                ))}
                {hasEu && <th scope="col">{onlyEu ? 'Upcoming' : 'Upcoming EU'}</th>}
                <th scope="col">ADI · ARfD</th>
                <th scope="col">{onlyEu ? 'Regulation' : 'Reference'}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(r => {
                const id = r.pesticide_residue_id;
                const byCode = checks.get(id) ?? {};
                const entered = (values[id] ?? '').trim() !== '';
                const isOpen = openId === id;
                const anyFail = codes.some(c => byCode[c]?.result?.verdict === 'fail');
                const anyWarn = codes.some(c => byCode[c]?.result?.verdict === 'limit' || byCode[c]?.upcomingFail);
                const tone = anyFail ? ' pest-row--fail' : anyWarn ? ' pest-row--warn' : '';
                const anyInvalid = codes.some(c => byCode[c]?.invalid);
                const eu = r.cells.EU;
                const toggle = () => setOpenId(isOpen ? null : id);
                return (
                  <React.Fragment key={id}>
                    <tr className={`pest-row${tone}${isOpen ? ' pest-row--open' : ''}`} onClick={toggle}>
                      <td>
                        <div className="pest-subst">
                          <button
                            type="button"
                            className="pest-subst__name"
                            aria-expanded={isOpen}
                            onClick={e => {
                              e.stopPropagation();
                              toggle();
                            }}
                          >
                            {r.display}
                          </button>
                          {r.markers.map(m => (
                            <abbr key={m} className="pest-marker" title={MARKER_TITLES[m] ?? 'Marker from the EU Pesticides Database'}>
                              {m}
                            </abbr>
                          ))}
                          {r.banned && <span className="pest-pill pest-pill--banned">Not approved</span>}
                        </div>
                      </td>
                      {codes.map(code => {
                        const cell = r.cells[code] ?? NO_CELL;
                        const meta = metaByCode.get(code);
                        if (cell.unmapped) {
                          return (
                            <td key={`mrl-${code}`} className="pest-mrl pest-mrl--unmapped">
                              <abbr title={unmappedTitle(meta, code)}>
                                {meta?.pending_review ? 'mapping pending' : 'not mapped'}
                              </abbr>
                            </td>
                          );
                        }
                        return (
                          <td key={`mrl-${code}`} className={`pest-mono pest-mrl pest-mrl--${cell.mrl.kind}`}>
                            {limitText(cell.mrl, code)}
                            {code !== 'EU' && cell.limit?.applicability && (
                              <abbr className="pest-marker" title={`Applies only: ${cell.limit.applicability}`}>†</abbr>
                            )}
                          </td>
                        );
                      })}
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={`pest-value${anyInvalid ? ' pest-value--invalid' : ''}`}
                          placeholder="—"
                          aria-label={`Lab value for ${r.display}, mg/kg`}
                          aria-invalid={anyInvalid || undefined}
                          value={values[id] ?? ''}
                          onChange={e => setValue(id, e.target.value)}
                        />
                      </td>
                      {codes.map(code => {
                        const c = byCode[code] ?? NO_CHECK;
                        return (
                          <td key={`verdict-${code}`}>
                            {c.invalid ? (
                              <span className="pest-pill pest-pill--muted">Not a number</span>
                            ) : c.unmapped && entered ? (
                              <span className="pest-pill pest-pill--unmapped" title={unmappedTitle(metaByCode.get(code), code)}>
                                Not mapped
                              </span>
                            ) : c.result ? (
                              c.result.verdict === 'nolimit' && code !== 'EU' && hasGroupLimit(code, id) ? (
                                <span
                                  className="pest-pill pest-pill--group"
                                  title={`${code} sets no tolerance for this substance on the commodity itself, but a crop-group tolerance covers it (see the group list under the table and in the row detail). Group tolerances are shown, not judged.`}
                                >
                                  Group tolerance only · not judged
                                </span>
                              ) : c.result.verdict === 'nolimit' && code !== 'EU' && c.detected ? (
                                <span
                                  className="pest-pill pest-pill--notol"
                                  title={`A residue was found but ${code} has no tolerance on record for this substance on this commodity, and no crop-group tolerance covers it. Without a tolerance or exemption the residue is not permitted.`}
                                >
                                  No {code} tolerance · detected
                                </span>
                              ) : (
                                <span className={`pest-pill pest-pill--${c.result.verdict}`}>{verdictText(c.result, code)}</span>
                              )
                            ) : (
                              <span className="pest-dash">—</span>
                            )}
                          </td>
                        );
                      })}
                      {hasEu && (
                        <td>
                          {eu?.limit?.upcoming && eu.upMrl ? (
                            <span className={`pest-pill pest-pill--upcoming${byCode.EU?.upcomingFail ? ' pest-pill--upcoming-fail' : ''}`}>
                              {mrlText(eu.upMrl)} from {formatISODate(eu.limit.upcoming.effective_date) || 'a later date'}
                            </span>
                          ) : (
                            <span className="pest-dash">—</span>
                          )}
                        </td>
                      )}
                      <td className="pest-tox">
                        <div><span className="pest-tox__label">ADI</span> {shortTox(r.adi)}</div>
                        <div><span className="pest-tox__label">ARfD</span> {shortTox(r.arfd)}</div>
                      </td>
                      <td className="pest-mono pest-reg">
                        {codes.some(code => r.cells[code]?.limit?.legal_ref) ? (
                          <div className="pest-ref">
                            {codes.map(code => {
                              const limit = r.cells[code]?.limit;
                              if (!limit?.legal_ref) return null;
                              return (
                                <span key={code}>
                                  {!onlyEu && <span className="pest-ref__code">{code}</span>}
                                  {code === 'EU' ? (
                                    <Link
                                      to={`/regulation/${encodeURIComponent(limit.legal_ref)}`}
                                      onClick={e => e.stopPropagation()}
                                    >
                                      {refLabel(code, limit.legal_ref)}
                                    </Link>
                                  ) : limit.reference_url ? (
                                    <a
                                      href={limit.reference_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={e => e.stopPropagation()}
                                    >
                                      {refLabel(code, limit.legal_ref)} ↗
                                    </a>
                                  ) : (
                                    refLabel(code, limit.legal_ref)
                                  )}
                                </span>
                              );
                            })}
                          </div>
                        ) : (
                          <span className="pest-dash">—</span>
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="pest-detail">
                        <td colSpan={colCount}>
                          <RowDetail
                            row={r}
                            codes={codes}
                            checks={byCode}
                            metaByCode={metaByCode}
                            groupsByResidue={groupsByResidue}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {shown.length === 0 && <div className="pest-empty">{emptyText}</div>}
        </div>

        {foreignCodes.map(code => {
          const meta = metaByCode.get(code);
          const groups = meta?.group_limits ?? [];
          if (groups.length === 0) return null;
          return (
            <details key={code} className="pest-groups">
              <summary>
                {meta?.name ?? code}: {groups.length} limit{groups.length === 1 ? '' : 's'} apply via commodity groups
                {' '}(shown for reference, not used for the verdict)
              </summary>
              <ul className="pest-groups__list">
                {groups.map(g => (
                  <li key={`${g.pesticide_residue_id}|${g.legal_ref}|${g.group_commodity}`}>
                    <strong>{g.substance_name}</strong>
                    {' · '}{g.group_commodity}
                    {' · '}<span className="pest-mono">{g.is_exempt ? 'exempt' : `${g.value_text ?? '—'} mg/kg`}</span>
                    {' · '}
                    {g.reference_url ? (
                      <a href={g.reference_url} target="_blank" rel="noopener noreferrer">{refLabel(code, g.legal_ref)} ↗</a>
                    ) : (
                      refLabel(code, g.legal_ref)
                    )}
                  </li>
                ))}
              </ul>
            </details>
          );
        })}

        <div className={`pest-summary${codes.length > 1 ? ' pest-summary--multi' : ''}`}>
          {counts.entered === 0 ? (
            <>
              <span className="pest-summary__label">Shipment</span>
              <span className="pest-summary__hint">
                {allUnmapped
                  ? 'No selected legislation is mapped to this product, so nothing can be checked yet.'
                  : `Enter lab values to check this shipment against today's ${onlyEu ? 'MRLs' : `${checkable.join(' and ')} limits`}.`}
              </span>
            </>
          ) : codes.length === 1 ? (
            <>
              <span className="pest-summary__label">{onlyEu ? 'Shipment' : `Shipment · ${codes[0]}`}</span>
              <SummaryPills code={codes[0]} s={summary[codes[0]]} meta={metaByCode.get(codes[0])} />
            </>
          ) : (
            codes.map(code => (
              <div key={code} className="pest-summary__line">
                <span className="pest-summary__label">Shipment · {code}</span>
                <SummaryPills code={code} s={summary[code]} meta={metaByCode.get(code)} />
              </div>
            ))
          )}
          <span className="pest-summary__count">
            {counts.entered} value{counts.entered === 1 ? '' : 's'} entered
          </span>
        </div>
      </section>

      <p className="mrl-footnote">
        * MRL set at the limit of analytical determination (LOD). Click a row for the regulation, EUR-Lex link,
        residue definition and footnotes. Verdicts compare your value with the MRL in force today and are a
        screening aid, not an official compliance decision.
        {!onlyEu && ' Each legislation is judged separately: a shipment can pass in one and fail in another.'}
      </p>
      {foreignCodes.map(code => {
        const meta = metaByCode.get(code);
        if (!meta) return null;
        return (
          <p key={code} className="mrl-footnote">
            {meta.name}: {meta.attribution ?? meta.authority}
            {meta.licence ? `. ${meta.licence}` : ''}
            {meta.last_loaded_at ? `. Register checked ${formatISODate(meta.last_loaded_at.slice(0, 10))}` : ''}.
            {code === 'US' && ' US tolerances are published in parts per million, which equals mg/kg. Without a tolerance or an exemption, a residue is not permitted in the US.'}
          </p>
        );
      })}
    </div>
  );
}
